import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { DECISION_STATUSES } from '../config/rateShopConstants';
import { DEFAULT_DECISION_FILTERS } from '../hooks/useRateShopDecisions';
import { money, primaryOrderId, secondaryOrderIds } from '../lib/describe';
import { storeChoices } from '../lib/storeHelpers';
import StoreLabel from '../components/StoreLabel';
import {
  dateTime,
  LoadError,
  Loading,
  RateLabel,
  RefreshButton,
  StatusBadge
} from '../components/Common';
import DecisionDrawer from '../modals/DecisionDrawer';

const LogTab = ({ data, canEdit, stores }) => {
  const {
    decisions,
    filters,
    setFilters,
    loading,
    error,
    refresh,
    loadedCount,
    loadMore,
    loadingMore,
    hasMore,
    getDecision,
    deleteDecision
  } = data;
  const [search, setSearch] = useState(filters.search);
  const [minSavings, setMinSavings] = useState(filters.minSavings ?? '');
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setFilters(prev =>
          prev.search === search ? prev : { ...prev, search }
        ),
      400
    );
    return () => clearTimeout(timer);
  }, [search, setFilters]);
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setFilters(prev => ({
          ...prev,
          minSavings: minSavings === '' ? null : Number(minSavings)
        })),
      400
    );
    return () => clearTimeout(timer);
  }, [minSavings, setFilters]);
  const change = patch => setFilters(prev => ({ ...prev, ...patch }));

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Decision log</h5>
          <RefreshButton onClick={refresh} loading={loading} />
        </Card.Header>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col lg={4}>
              <Form.Group controlId="rate-shop-log-search">
                <Form.Label>Find an order</Form.Label>
                <Form.Control
                  placeholder="Shipment id (se-…), purchase-order number, or marketplace order id"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col sm={6} lg={4}>
              <Form.Group controlId="rate-shop-log-status">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={e => change({ status: e.target.value })}
                >
                  <option value="">All statuses</option>
                  {DECISION_STATUSES.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} lg={4}>
              <Form.Group controlId="rate-shop-log-store">
                <Form.Label>Store</Form.Label>
                <Form.Select
                  value={filters.storeId}
                  onChange={e => change({ storeId: e.target.value })}
                >
                  <option value="">All stores</option>
                  {storeChoices(
                    stores,
                    filters.storeId ? [filters.storeId] : []
                  ).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6} lg={4}>
              <Form.Label htmlFor="rate-shop-log-from">From date</Form.Label>
              <div>
                <DatePicker
                  id="rate-shop-log-from"
                  className="form-control"
                  selected={filters.from}
                  onChange={date => change({ from: date })}
                  selectsStart
                  startDate={filters.from}
                  endDate={filters.to}
                  maxDate={filters.to}
                  isClearable
                  placeholderText="Any start date"
                />
              </div>
            </Col>
            <Col sm={6} lg={4}>
              <Form.Label htmlFor="rate-shop-log-to">Through date</Form.Label>
              <div>
                <DatePicker
                  id="rate-shop-log-to"
                  className="form-control"
                  selected={filters.to}
                  onChange={date => {
                    const end = date ? new Date(date) : null;
                    if (end) end.setHours(23, 59, 59, 999);
                    change({ to: end });
                  }}
                  selectsEnd
                  startDate={filters.from}
                  endDate={filters.to}
                  minDate={filters.from}
                  isClearable
                  placeholderText="Any end date"
                />
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <Form.Group controlId="rate-shop-log-savings">
                <Form.Label>Savings ≥ $</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={minSavings}
                  onChange={e => setMinSavings(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col sm={6} lg={1} className="d-flex align-items-end">
              <Button
                variant="link"
                className="px-0"
                onClick={() => {
                  setSearch('');
                  setMinSavings('');
                  setFilters({ ...DEFAULT_DECISION_FILTERS });
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>
          <p className="small text-muted mb-0">
            Shipment id (se-…), purchase-order number, or marketplace order id.
            Search uses an exact match, checking purchase-order numbers first.
            Date, status and store filters run on the server. Savings filters
            the loaded decisions.
          </p>
          <LoadError error={error} />
        </Card.Body>
        {loading ? (
          <Loading />
        ) : (
          <div className="table-responsive">
            <Table hover className="small align-middle mb-0">
              <thead>
                <tr>
                  <th>Decided at</th>
                  <th>Order #</th>
                  <th>Store</th>
                  <th>Status</th>
                  <th>Winner</th>
                  <th>Current</th>
                  <th>Savings</th>
                  <th>Run</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map(decision => (
                  <tr
                    key={decision.id}
                    onClick={() => setSelected(decision.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="text-nowrap">
                      {dateTime(decision.decidedAt)}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-start"
                        onClick={e => {
                          e.stopPropagation();
                          setSelected(decision.id);
                        }}
                      >
                        <strong>{primaryOrderId(decision)}</strong>
                      </Button>
                      {secondaryOrderIds(decision).map(value => (
                        <div key={value} className="small text-muted">
                          {value === decision.id
                            ? 'Shipment: '
                            : 'Marketplace: '}
                          {value}
                        </div>
                      ))}
                    </td>
                    <td>
                      <StoreLabel
                        id={decision.storeId || decision.context?.storeId}
                        stores={stores}
                      />
                    </td>
                    <td>
                      <StatusBadge status={decision.status} />
                    </td>
                    <td>
                      <RateLabel rate={decision.winner} />
                    </td>
                    <td>
                      <RateLabel rate={decision.currentRate} carrier={false} />
                    </td>
                    <td
                      className={
                        decision.savings?.vsCurrent > 0
                          ? 'text-success fw-bold'
                          : ''
                      }
                    >
                      {money(decision.savings?.vsCurrent)}
                    </td>
                    <td className="text-break">{decision.runId || '—'}</td>
                  </tr>
                ))}
                {!decisions.length && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-muted">
                      No decisions match these filters. Try clearing filters or
                      wait for the next run.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span className="small text-muted">
            {hasMore
              ? `Showing first ${loadedCount} matching decisions; more may be available.`
              : `${loadedCount} decisions loaded.`}{' '}
            {decisions.length} shown after savings filter.
          </span>
          {hasMore && (
            <Button
              size="sm"
              variant="outline-primary"
              disabled={loading || loadingMore}
              onClick={loadMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </Card.Footer>
      </Card>
      {selected && (
        <DecisionDrawer
          key={selected}
          id={selected}
          getDecision={getDecision}
          deleteDecision={deleteDecision}
          canEdit={canEdit}
          stores={stores}
          onHide={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default LogTab;
